import { IntroductionOutcomeEnum } from '@platform/client/schemas';
import { Icon } from '@platform/components';
import { MyProjectsModalProps } from '.';
import FlexibleLink from '../../../../util/FlexibleLink';
import { ProjectType } from '../../index';

export const ProIntroductionModalContent = (props: MyProjectsModalProps) => {
  const { userType, cardData } = props;
  if (userType !== 'experteer') {
    throw new Error('ProIntroductionModalContent is only for experteers');
  }
  const { introduction, organization, project, repTimezone } = cardData;
  const isCustom = !project;
  const userInitiatedIntro = introduction.initiationType === 'Experteer';
  const introExpirationDate = new Date(
    new Date(introduction.createdAt).getTime() + 7 * 24 * 60 * 60 * 1000,
  );

  return (
    <div className='flex flex-col gap-6 text-md' data-testid='pro-introduction-modal'>
      <div className='flex flex-col md:grid md:grid-cols-2 gap-4'>
        <div className='flex flex-col gap-2' data-testid='introduction-modal-organization'>
          <div className='font-medium text-gray-500'>Organization</div>
          <FlexibleLink
            newTab={true}
            className='text-sm font-semibold text-brand-600'
            href={`/platform/directory/organizations/${organization.id}`}
            type='internal'
          >
            {organization.name}
          </FlexibleLink>
        </div>
        <div className='flex flex-col gap-2' data-testid='introduction-modal-timezone'>
          <div className='font-medium text-gray-500'>Timezone</div>
          <div className='text-sm font-semibold'>{repTimezone}</div>
        </div>
        <div className='flex flex-col gap-2' data-testid='introduction-modal-project'>
          <div className='font-medium text-gray-500'>Project</div>
          {!isCustom && (
            <FlexibleLink
              newTab={true}
              type='internal'
              className='text-sm font-semibold text-brand-600'
              href={`/platform/directory/projects/${project.id}`}
            >
              {project.name}
            </FlexibleLink>
          )}
          {isCustom && <div className='text-sm font-semibold'>Custom Project</div>}
        </div>
        <div className='flex flex-col gap-2' data-testid='introduction-modal-role'>
          <div className='font-medium text-gray-500'>Role</div>
          <div className='text-sm font-semibold'>
            {project?.roleType ? ProjectType[project.roleType] : 'Custom'}
          </div>
        </div>
      </div>
      {isCustom && introduction.outcome === IntroductionOutcomeEnum.accepted && (
        <div className='bg-secondary-brand-25 border border-gray-200 p-4 font-gray-500 text-sm flex justify-left gap-4 items-center'>
          <Icon type='alert-circle' />
          This organization wants to collaborate on a custom project with you. That means you'll
          discuss potential projects together over a Discovery Call.
        </div>
      )}
      <div className='bg-gray-25 py-3 px-4 border border-gray-200 rounded-lg font-gray-700 min-h-44 break-words'>
        {introduction.personalMessage}
      </div>
      {introduction.outcome === IntroductionOutcomeEnum.expired && !userInitiatedIntro && (
        <div className='bg-error-25 border border-gray-200 p-4 font-gray-500 text-sm flex justify-left gap-4 items-center'>
          <Icon type='alert-triangle' />
          {/* TODO set to 7 days out */}
          {`This introduction expired ${introExpirationDate.toLocaleDateString('en-CA', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}. You have 7 days to respond to new introductions`}
        </div>
      )}
      {introduction.outcome === IntroductionOutcomeEnum.declined &&
        !userInitiatedIntro &&
        !cardData.assignmentDiscoveryId && (
          <div className='bg-warning-25 border border-gray-200 p-4 font-gray-500 text-sm flex justify-left gap-4 items-center'>
            <Icon type='alert-circle' />
            {`You declined this introduction on ${new Date(
              introduction.statusChangedAt,
            ).toLocaleDateString('en-CA', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}.`}
          </div>
        )}
      {introduction.outcome === IntroductionOutcomeEnum.declined &&
        !userInitiatedIntro &&
        cardData.assignmentDiscoveryId && (
          <div className='bg-warning-25 border border-gray-200 p-4 font-gray-500 text-sm flex justify-left gap-4 items-center'>
            <Icon type='alert-circle' />
            {`You declined to match on ${new Date(introduction.statusChangedAt).toLocaleDateString(
              'en-CA',
              {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              },
            )}.`}
          </div>
        )}
      {introduction.outcome === IntroductionOutcomeEnum.accepted && (
        <div className='bg-secondary-brand-25 border border-gray-200 p-4 font-gray-500 text-sm flex justify-left gap-4 items-center'>
          <Icon type='alert-circle' />
          Remember, you can connect with more than one organization, you aren't committing to
          working together yet.{' '}
        </div>
      )}
    </div>
  );
};
